export const ROLES = {
    SUPERADMIN: 'superadmin',
    ADMIN: 'admin',
    EMPLOYER: 'employer',
    BUSINESS_OWNER: 'business_owner',
    MANAGER: 'manager',
    SUPERVISOR: 'supervisor',
    EMPLOYEE: 'employee'
}

export const PERMISSIONS = {
    VIEW_DASHBOARD: 'view_dashboard',
    VIEW_SUPERADMIN_DASHBOARD: 'view_superadmin_dashboard',
    MANAGE_EMPLOYEES: 'manage_employees',
    MANAGE_ROSTER: 'manage_roster',
    TRACK_ATTENDANCE: 'track_attendance',
    MANAGE_LEAVE: 'manage_leave',
    MANAGE_PAYROLL: 'manage_payroll',
    MANAGE_BRANCHES: 'manage_branches',
    MANAGE_SHIFTS: 'manage_shifts',
    MANAGE_SETTINGS: 'manage_settings',
    VIEW_ALERTS: 'view_alerts',
    REGISTER_FACES: 'register_faces',
    MANAGE_ROLES: 'manage_roles',
    VIEW_ANNOUNCEMENTS: 'view_announcements'
}

/** Each app page is either view only (Retrieve) or full Create, Retrieve, Update, Delete. */
export const PAGE_ACCESS = {
    NONE: 'none',
    VIEW: 'view',
    CRUD: 'crud',
}

export const ROLE_PAGES = [
    { id: 'dashboard', section: 'Overview', label: 'Dashboard', permission: PERMISSIONS.VIEW_DASHBOARD },
    { id: 'attendance', section: 'Attendance', label: 'Attendance', permission: null },
    { id: 'history', section: 'Attendance', label: 'History', permission: null },
    { id: 'tracking', section: 'Attendance', label: 'Tracking', permission: PERMISSIONS.TRACK_ATTENDANCE },
    { id: 'staff-activity', section: 'Attendance', label: 'Staff Activity', permission: PERMISSIONS.TRACK_ATTENDANCE },
    { id: 'calendar', section: 'Attendance', label: 'Calendar', permission: PERMISSIONS.TRACK_ATTENDANCE },
    { id: 'time-adjustments', section: 'Attendance', label: 'Time Adjustments', permission: PERMISSIONS.TRACK_ATTENDANCE },
    { id: 'employees', section: 'Workforce', label: 'Employees', permission: PERMISSIONS.MANAGE_EMPLOYEES },
    { id: 'performance', section: 'Workforce', label: 'Performance & KPI', permission: PERMISSIONS.MANAGE_EMPLOYEES },
    { id: 'roles', section: 'Workforce', label: 'Role Management', permission: PERMISSIONS.MANAGE_ROLES },
    { id: 'face-registration', section: 'Workforce', label: 'Face Registration', permission: PERMISSIONS.REGISTER_FACES },
    { id: 'roster', section: 'Scheduling', label: 'Roster', permission: PERMISSIONS.MANAGE_ROSTER },
    { id: 'task-scheduler', section: 'Scheduling', label: 'Task Scheduler', permission: null },
    { id: 'task-board', section: 'Scheduling', label: 'Task Board', permission: null },
    { id: 'my-roster', section: 'Scheduling', label: 'My Roster', permission: null },
    { id: 'shifts', section: 'Scheduling', label: 'Shifts', permission: PERMISSIONS.MANAGE_SHIFTS },
    { id: 'leave-management', section: 'Leave', label: 'Leave Management', permission: PERMISSIONS.MANAGE_LEAVE },
    { id: 'leave-request', section: 'Leave', label: 'Leave Request', permission: null },
    { id: 'branches', section: 'Organization', label: 'Branches', permission: PERMISSIONS.MANAGE_BRANCHES },
    { id: 'announcements', section: 'Organization', label: 'Announcements', permission: PERMISSIONS.VIEW_ANNOUNCEMENTS },
    { id: 'payroll', section: 'Organization', label: 'Payroll', permission: PERMISSIONS.MANAGE_PAYROLL },
    { id: 'holidays', section: 'Organization', label: 'Public Holidays', permission: null },
    { id: 'alerts', section: 'System', label: 'Alerts', permission: PERMISSIONS.VIEW_ALERTS },
    { id: 'settings', section: 'System', label: 'Settings', permission: PERMISSIONS.MANAGE_SETTINGS },
    { id: 'profile', section: 'System', label: 'My Profile', permission: null },
]

export const pageAccessLabel = (mode) => {
    if (mode === PAGE_ACCESS.CRUD) return 'Create, Retrieve, Update, Delete'
    if (mode === PAGE_ACCESS.VIEW) return 'View only'
    return 'No access'
}

export const allowsPageAction = (mode, action) => {
    if (mode === PAGE_ACCESS.CRUD) return true
    if (mode === PAGE_ACCESS.VIEW) return action === 'retrieve'
    return false
}

const defaultModeForPermission = (permission) => (
    String(permission || '').startsWith('view_') ? PAGE_ACCESS.VIEW : PAGE_ACCESS.CRUD
)

/** Turn the older permission list into a page-by-page access map. */
export const pageAccessFromPermissions = (permissions = []) => {
    const granted = new Set(permissions || [])
    return Object.fromEntries(ROLE_PAGES.map((page) => {
        if (!page.permission) return [page.id, PAGE_ACCESS.VIEW]
        return [page.id, granted.has(page.permission) ? defaultModeForPermission(page.permission) : PAGE_ACCESS.NONE]
    }))
}

/** Pages set to view or full access keep the menu permission that opens them. */
export const permissionsFromPageAccess = (pageAccess = {}) => {
    const permissions = new Set()
    for (const page of ROLE_PAGES) {
        const mode = pageAccess[page.id]
        if (page.permission && (mode === PAGE_ACCESS.VIEW || mode === PAGE_ACCESS.CRUD)) permissions.add(page.permission)
    }
    return [...permissions]
}

export const findCustomRole = (userRole, customRoles = []) => {
    const normalizedRole = (userRole || '').toString().toLowerCase()
    return (customRoles || []).find((entry) =>
        entry.id === userRole ||
        entry.name === userRole ||
        entry.id === normalizedRole ||
        entry.name === normalizedRole ||
        (entry.overrides && (entry.overrides === userRole || entry.overrides === normalizedRole))
    ) || null
}

/** Saved page access, or null when the role still uses the older permission list. */
export const pageAccessMode = (userRole, pageId, customRoles = []) => {
    const customRole = findCustomRole(userRole, customRoles)
    if (!customRole?.pageAccess || typeof customRole.pageAccess !== 'object') return null
    return customRole.pageAccess[pageId] || PAGE_ACCESS.NONE
}

/**
 * Effective page access for menu/route guards.
 * Prefers a saved pageAccess map; otherwise derives from the role permission list.
 */
export const resolvePageAccess = (userRole, pageId, customRoles = []) => {
    const saved = pageAccessMode(userRole, pageId, customRoles)
    if (saved) return saved

    const page = ROLE_PAGES.find((entry) => entry.id === pageId)
    if (!page) return PAGE_ACCESS.NONE
    if (!page.permission) return PAGE_ACCESS.VIEW
    return hasPermission(userRole, page.permission, customRoles)
        ? defaultModeForPermission(page.permission)
        : PAGE_ACCESS.NONE
}

export const DEFAULT_ROLE_PERMISSIONS = {
    [ROLES.SUPERADMIN]: Object.values(PERMISSIONS),
    [ROLES.ADMIN]: Object.values(PERMISSIONS).filter(p => p !== PERMISSIONS.VIEW_SUPERADMIN_DASHBOARD),
    [ROLES.EMPLOYER]: Object.values(PERMISSIONS).filter(p => p !== PERMISSIONS.VIEW_SUPERADMIN_DASHBOARD),
    [ROLES.BUSINESS_OWNER]: [
        PERMISSIONS.VIEW_DASHBOARD,
        PERMISSIONS.MANAGE_EMPLOYEES,
        PERMISSIONS.MANAGE_ROSTER,
        PERMISSIONS.TRACK_ATTENDANCE,
        PERMISSIONS.MANAGE_LEAVE,
        PERMISSIONS.MANAGE_PAYROLL,
        PERMISSIONS.MANAGE_BRANCHES,
        PERMISSIONS.MANAGE_SHIFTS,
        PERMISSIONS.MANAGE_SETTINGS,
        PERMISSIONS.VIEW_ALERTS,
        PERMISSIONS.REGISTER_FACES,
        PERMISSIONS.VIEW_ANNOUNCEMENTS
    ],
    [ROLES.MANAGER]: [
        PERMISSIONS.VIEW_DASHBOARD,
        PERMISSIONS.MANAGE_EMPLOYEES,
        PERMISSIONS.MANAGE_ROSTER,
        PERMISSIONS.TRACK_ATTENDANCE,
        PERMISSIONS.MANAGE_LEAVE,
        PERMISSIONS.MANAGE_PAYROLL,
        PERMISSIONS.MANAGE_BRANCHES,
        PERMISSIONS.MANAGE_SHIFTS,
        PERMISSIONS.MANAGE_SETTINGS,
        PERMISSIONS.VIEW_ALERTS,
        PERMISSIONS.REGISTER_FACES,
        PERMISSIONS.VIEW_ANNOUNCEMENTS
    ],
    [ROLES.SUPERVISOR]: [
        PERMISSIONS.VIEW_DASHBOARD,
        PERMISSIONS.TRACK_ATTENDANCE,
        PERMISSIONS.MANAGE_ROSTER,
        PERMISSIONS.VIEW_ALERTS,
        PERMISSIONS.REGISTER_FACES,
        PERMISSIONS.VIEW_ANNOUNCEMENTS
    ],
    [ROLES.EMPLOYEE]: [
        PERMISSIONS.VIEW_ANNOUNCEMENTS
    ]
}

/** Day-to-day admin who must be able to edit Salary Slip 2.0 commission tables. */
export const PAYROLL_RULES_EDITOR_EMAIL = 'work.kuzafri@gmail.com'

/**
 * Sales profit for salary slips is limited to the employer role and this account.
 * A custom role that overrides employer counts as the employer.
 */
export const canFetchSalesProfit = (user = {}, customRoles = []) => {
  const email = String(user?.email || '').trim().toLowerCase()
  if (email === PAYROLL_RULES_EDITOR_EMAIL) return true

  const role = String(user?.role || '').trim().toLowerCase()
  if (role === 'employer') return true

  const customRole = (customRoles || []).find((entry) =>
    entry.id === user?.role ||
    entry.id === role ||
    entry.name === user?.role ||
    entry.name === role ||
    (entry.overrides && (entry.overrides === user?.role || entry.overrides === role))
  )
  return String(customRole?.overrides || '').toLowerCase() === 'employer'
}

/**
 * Who can open Payroll Rules and save the commission tables.
 * The named admin and the employer role are allowed even when a custom role
 * has stripped manage_payroll. Other staff still need that permission.
 */
export const canEditPayrollRules = (user = {}, customRoles = []) => {
  const email = String(user?.email || '').trim().toLowerCase()
  if (email === PAYROLL_RULES_EDITOR_EMAIL) return true

  const role = String(user?.role || '').trim().toLowerCase()
  if (role === 'employer' || role === 'admin') return true

  const customRole = (customRoles || []).find((entry) =>
    entry.id === user?.role ||
    entry.id === role ||
    entry.name === user?.role ||
    entry.name === role ||
    (entry.overrides && (entry.overrides === user?.role || entry.overrides === role))
  )
  const override = String(customRole?.overrides || '').toLowerCase()
  if (override === 'employer' || override === 'admin') return true

  if (role === 'superadmin') return false
  return hasPermission(role, PERMISSIONS.MANAGE_PAYROLL, customRoles)
}

export const hasPermission = (userRole, permission, customRoles = []) => {
    const normalizedRole = (userRole || '').toString().toLowerCase()

    // Check custom roles FIRST (to allow overrides)
    // We check for ID match, Name match, or Overrides field match
    const customRole = customRoles.find(r => 
        r.id === userRole ||
        r.name === userRole ||
        r.id === normalizedRole ||
        r.name === normalizedRole ||
        (r.overrides && (r.overrides === userRole || r.overrides === normalizedRole))
    )

    if (customRole) {
        return customRole.permissions?.includes(permission) || false
    }

    // Fallback to static roles if no custom override exists
    if (DEFAULT_ROLE_PERMISSIONS[userRole]?.includes(permission) || DEFAULT_ROLE_PERMISSIONS[normalizedRole]?.includes(permission)) {
        return true
    }

    // Legacy management roles should keep full management sidebar access
    // (except superadmin-only dashboard).
    if (['employer', 'hr', 'administrator'].includes(normalizedRole)) {
        return permission !== PERMISSIONS.VIEW_SUPERADMIN_DASHBOARD
    }

    return false
}

export const isAtLeastManager = (role, customRoles = []) => {
    const normalizedRole = (role || '').toString().toLowerCase()

    // Check custom role first for management flag
    const customRole = customRoles.find(r => 
        r.id === role ||
        r.name === role ||
        r.id === normalizedRole ||
        r.name === normalizedRole ||
        (r.overrides && (r.overrides === role || r.overrides === normalizedRole))
    )
    
    if (customRole) {
        return customRole.isManagement || false
    }

    // Fallback to default logic
    if ([ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.EMPLOYER, ROLES.BUSINESS_OWNER, ROLES.MANAGER, ROLES.SUPERVISOR].includes(role)) {
        return true
    }
    if (['superadmin', 'admin', 'employer', 'business_owner', 'manager', 'supervisor', 'hr', 'administrator'].includes(normalizedRole)) {
        return true
    }

    return false
}

export const getRoleLabel = (role, customRoles = []) => {
    // Check if there's a custom role overriding this system role
    const customRole = customRoles.find(r => 
        r.id === role || 
        r.name === role || 
        (r.overrides && r.overrides === role)
    )

    if (customRole) {
        return customRole.name
    }

    switch (role) {
        case ROLES.SUPERADMIN: return 'Super Admin'
        case ROLES.ADMIN: return 'Administrator'
        case ROLES.EMPLOYER: return 'Employer'
        case ROLES.BUSINESS_OWNER: return 'Business Owner'
        case ROLES.MANAGER: return 'Manager'
        case ROLES.SUPERVISOR: return 'Supervisor'
        case ROLES.EMPLOYEE: return 'Employee'
        default:
            if (role === 'hr') return 'HR'
            if (role === 'administrator') return 'Administrator'
            return role || 'Employee'
    }
}
